const effectRootSelector = '[data-printale-homepage-effects]';
const typewriterSelector = '[data-printale-typewriter]';

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function initSpotlight(root) {
  if (prefersReducedMotion()) return;

  let rafId = 0;
  let nextX = 50;
  let nextY = 35;
  let currentX = nextX;
  let currentY = nextY;

  function render() {
    currentX += (nextX - currentX) * 0.18;
    currentY += (nextY - currentY) * 0.18;
    root.style.setProperty('--printale-spotlight-x', `${currentX}%`);
    root.style.setProperty('--printale-spotlight-y', `${currentY}%`);
    rafId = window.requestAnimationFrame(render);
  }

  window.addEventListener('pointermove', (event) => {
    nextX = (event.clientX / Math.max(window.innerWidth, 1)) * 100;
    nextY = (event.clientY / Math.max(window.innerHeight, 1)) * 100;
    root.style.setProperty('--printale-spotlight-opacity', '1');
  }, { passive: true });

  window.addEventListener('pointerleave', () => {
    root.style.setProperty('--printale-spotlight-opacity', '0');
  });

  render();

  window.addEventListener('pagehide', () => {
    window.cancelAnimationFrame(rafId);
  }, { once: true });
}

function initTypewriter(element) {
  const words = (element.dataset.words || '')
    .split('|')
    .map((word) => word.trim())
    .filter(Boolean);

  if (!words.length) return;

  if (prefersReducedMotion()) {
    element.textContent = words[0];
    return;
  }

  let wordIndex = 0;
  let characterIndex = 0;
  let deleting = false;

  function tick() {
    const word = words[wordIndex % words.length];
    element.textContent = word.slice(0, characterIndex);

    if (!deleting && characterIndex < word.length) {
      characterIndex += 1;
      window.setTimeout(tick, 72);
      return;
    }

    if (!deleting && characterIndex === word.length) {
      deleting = true;
      window.setTimeout(tick, 1300);
      return;
    }

    if (deleting && characterIndex > 0) {
      characterIndex -= 1;
      window.setTimeout(tick, 38);
      return;
    }

    deleting = false;
    wordIndex += 1;
    window.setTimeout(tick, 180);
  }

  tick();
}

// WebGL click-sparkles removed — DOM bursts (initDomClickBursts) + site-polish
// sparkles provide equivalent effect with far lower GPU cost.

function initDomClickBursts() {
  if (prefersReducedMotion()) return;

  window.addEventListener('pointerdown', (event) => {
    if (event.button !== 0) return;

    const burst = document.createElement('span');
    burst.className = 'printale-click-burst';
    burst.style.left = `${event.clientX}px`;
    burst.style.top = `${event.clientY}px`;
    burst.setAttribute('aria-hidden', 'true');

    for (let index = 0; index < 12; index += 1) {
      const angle = (index / 12) * Math.PI * 2;
      const distance = 42 + Math.random() * 42;
      const spark = document.createElement('span');
      spark.className = 'printale-click-burst__spark';
      spark.style.setProperty('--spark-x', `${Math.cos(angle) * distance}px`);
      spark.style.setProperty('--spark-y', `${Math.sin(angle) * distance}px`);
      spark.style.setProperty('--spark-z', `${60 + Math.random() * 110}px`);
      spark.style.setProperty('--spark-size', `${5 + Math.random() * 7}px`);
      spark.style.setProperty('--spark-rotate', `${Math.random() * 180}deg`);
      burst.append(spark);
    }

    document.body.append(burst);
    window.setTimeout(() => burst.remove(), 920);
  }, { passive: true });
}

function initHomepageEffects() {
  const root = document.querySelector(effectRootSelector);
  if (root instanceof HTMLElement) {
    initSpotlight(root);
  }

  document.querySelectorAll(typewriterSelector).forEach((element) => {
    if (element instanceof HTMLElement) {
      initTypewriter(element);
    }
  });

  initDomClickBursts();

  // 🎬 Start the new cinematic enhancements
  initCinemaEnhancements();
}

/* ==========================================================================
   🎬 PRINTALE CINEMA UI/UX ENHANCEMENTS ENGINE
   ========================================================================== */

function initCinemaEnhancements() {
  // --- Grain always on — no audio, no toggle buttons ---
  const grainOverlay = document.getElementById('cinema-film-grain');
  if (grainOverlay) {
    grainOverlay.classList.add('active');
  }


  // --- B. Floating Projector Dust Particles (B1) ---
  const dustCanvas = document.createElement('canvas');
  dustCanvas.style.cssText = 'position: fixed; inset: 0; pointer-events: none; z-index: 2;';
  document.body.prepend(dustCanvas);

  const dustCtx = dustCanvas.getContext('2d');
  let dustParticles = [];
  const maxDust = 30;

  function resizeDustCanvas() {
    dustCanvas.width = window.innerWidth;
    dustCanvas.height = window.innerHeight;
  }
  window.addEventListener('resize', resizeDustCanvas);
  resizeDustCanvas();

  class DustParticle {
    constructor() {
      this.reset();
    }
    reset() {
      this.x = Math.random() * dustCanvas.width;
      this.y = Math.random() * dustCanvas.height;
      this.size = 0.5 + Math.random() * 2.2;
      this.speedX = (Math.random() - 0.5) * 0.35;
      this.speedY = -0.15 - Math.random() * 0.45;
      this.alpha = 0.1 + Math.random() * 0.45;
      this.alphaDir = Math.random() > 0.5 ? 0.005 : -0.005;
    }
    update() {
      this.x += this.speedX;
      this.y += this.speedY;
      this.alpha += this.alphaDir;

      if (this.alpha <= 0.05 || this.alpha >= 0.55) {
        this.alphaDir = -this.alphaDir;
      }
      // Loop around screen
      if (this.y < -10 || this.x < -10 || this.x > dustCanvas.width + 10) {
        this.reset();
        this.y = dustCanvas.height + 5;
      }
    }
    draw() {
      dustCtx.save();
      dustCtx.globalAlpha = this.alpha;
      // Faint golden/warm dust glow
      dustCtx.fillStyle = 'rgba(255, 235, 190, 0.7)';
      dustCtx.shadowBlur = 6;
      dustCtx.shadowColor = '#ffe8b4';
      dustCtx.beginPath();
      dustCtx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
      dustCtx.fill();
      dustCtx.restore();
    }
  }

  // Skip dust particles entirely on reduced-motion
  if (!prefersReducedMotion()) {
    for (let i = 0; i < maxDust; i++) {
      dustParticles.push(new DustParticle());
    }

    let dustRafId = null;

    function loopDust() {
      dustCtx.clearRect(0, 0, dustCanvas.width, dustCanvas.height);
      dustParticles.forEach(p => { p.update(); p.draw(); });
      dustRafId = requestAnimationFrame(loopDust);
    }

    // Pause loop when tab is hidden, resume when visible
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        cancelAnimationFrame(dustRafId);
        dustRafId = null;
      } else if (!dustRafId) {
        dustRafId = requestAnimationFrame(loopDust);
      }
    });

    dustRafId = requestAnimationFrame(loopDust);
  }


  // --- C. Scroll Interactions: Warmth, Clappers, Widescreen ---
  const clapperCards = document.querySelectorAll('.cinema-clapperboard-card');
  const letterboxWrappers = document.querySelectorAll('.cinema-letterbox-wrapper');
  const stickyMiniBar = document.querySelector('.cinema-sticky-mini-bar');
  const starsGlint = document.querySelectorAll('.cinema-star-glint');

  // Trigger Clappers on scroll
  const clapperObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('active-snap');
        clapperObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.35 });

  clapperCards.forEach(card => clapperObserver.observe(card));

  // Trigger Rating Stars Sparkle
  const starsObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('active-sparkle');
        starsObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.5 });

  starsGlint.forEach(stars => starsObserver.observe(stars));

  // Trigger Widescreen Cinematic bars on emotional section
  const letterboxObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('active-widescreen');
      } else {
        entry.target.classList.remove('active-widescreen');
      }
    });
  }, { threshold: 0.15 });

  letterboxWrappers.forEach(wrap => letterboxObserver.observe(wrap));

  // Scroll listener for: background warmth transition and sticky minibar
  // maxScroll cached and refreshed on resize to avoid forced reflow each scroll event
  let cachedMaxScroll = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
  let scrollTicking = false;

  window.addEventListener('resize', () => {
    cachedMaxScroll = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
  }, { passive: true });

  window.addEventListener('scroll', () => {
    if (scrollTicking) return;
    scrollTicking = true;
    requestAnimationFrame(() => {
      const scrollY = window.scrollY;
      const scrollPercent = scrollY / cachedMaxScroll;

      // B4: Dynamic Background Warmth
      if (!prefersReducedMotion()) {
        const warmthColor = interpolateColor('#0f0a0c', '#241014', scrollPercent);
        document.body.style.setProperty('--dynamic-bg-warmth', warmthColor);
      }

      // E3: Sticky Mini Bar visibility
      if (stickyMiniBar) {
        stickyMiniBar.classList.toggle('visible', scrollY > window.innerHeight * 0.8);
      }

      scrollTicking = false;
    });
  }, { passive: true });

  // Interpolate hex colors linearly
  function interpolateColor(color1, color2, factor) {
    const r1 = parseInt(color1.substring(1, 3), 16);
    const g1 = parseInt(color1.substring(3, 5), 16);
    const b1 = parseInt(color1.substring(5, 7), 16);

    const r2 = parseInt(color2.substring(1, 3), 16);
    const g2 = parseInt(color2.substring(3, 5), 16);
    const b2 = parseInt(color2.substring(5, 7), 16);

    const r = Math.round(r1 + factor * (r2 - r1));
    const g = Math.round(g1 + factor * (g2 - g1));
    const b = Math.round(b1 + factor * (b2 - b1));

    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  // --- F. Genre Switcher Glitch / Projector Flicker Hooks ---
  const genrePills = document.querySelectorAll('.printale-genre-pill');
  const posterWrapper = document.querySelector('.printale-poster-card-wrapper');
  
  genrePills.forEach(pill => {
    pill.addEventListener('click', () => {
      // 1. VHS static distortion & Film Burn flash (F1, F2)
      if (posterWrapper) {
        // Create custom VHS overlay element if not exists
        let vhs = posterWrapper.querySelector('.cinema-vhs-glitch');
        let burn = posterWrapper.querySelector('.cinema-film-burn');
        
        if (!vhs) {
          vhs = document.createElement('div');
          vhs.className = 'cinema-vhs-glitch';
          posterWrapper.appendChild(vhs);
        }
        if (!burn) {
          burn = document.createElement('div');
          burn.className = 'cinema-film-burn';
          posterWrapper.appendChild(burn);
        }

        vhs.classList.add('active');
        burn.classList.add('active');

        setTimeout(() => {
          vhs.classList.remove('active');
        }, 300);

        setTimeout(() => {
          burn.classList.remove('active');
        }, 500);
      }
    });
  });

  // Observe Carousel active center card triggers projector flicker (C2)
  const carouselTrack = document.querySelector('[data-filmstrip-track]');
  if (carouselTrack) {
    let lastActiveCardId = null;
    const observerCallback = (mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const card = mutation.target;
          if (card.classList.contains('is-active-center')) {
            const cardId = card.id || card.getAttribute('data-index');
            if (cardId !== lastActiveCardId) {
              lastActiveCardId = cardId;
              // Trigger Projector Flicker Pulse on card
              card.classList.add('projector-flicker-pulse');
              setTimeout(() => {
                card.classList.remove('projector-flicker-pulse');
              }, 220);
            }
          }
        }
      }
    };
    
    const carouselObserver = new MutationObserver(observerCallback);
    const cards = document.querySelectorAll('[class*="printale-filmstrip-card-"]');
    cards.forEach((card, idx) => {
      if (!card.id) card.id = `filmstrip-card-auto-${idx}`;
      carouselObserver.observe(card, { attributes: true, attributeFilter: ['class'] });
    });
  }
}

initHomepageEffects();
